import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import axios from "axios";
import { 
  Search, 
  MapPin,
  CheckCircle2,
  ArrowRight,
  Building2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const Suppliers = () => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers`);
      setSuppliers(response.data);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
    setLoading(false);
  };

  const filteredSuppliers = suppliers.filter(supplier => 
    supplier.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.categories.some(cat => cat.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="suppliers-page">
      {/* Header */}
      <div 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?w=1920&q=80')`
        }}
      >
        <div className="absolute inset-0 bg-[#0D4F8B]/80" />
        <div className="container-main relative z-10 text-center">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('nav.suppliers')}
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto">
            Connect with verified UAE suppliers for your East African business
          </p>
        </div>
      </div>

      <div className="container-main py-8">
        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search suppliers by name, location, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search suppliers"
              className="pl-10 input-style"
              data-testid="supplier-search"
            />
          </div>
        </div>

        {/* Suppliers Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="card-base">
                <CardContent className="p-6">
                  <Skeleton className="h-16 w-16 rounded-md mb-4" />
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No suppliers found</h3>
            <p className="text-slate-500">Try adjusting your search query</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSuppliers.map((supplier) => (
              <Card key={supplier.id} className="card-base card-interactive hover-lift" data-testid={`supplier-${supplier.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 bg-[#0D4F8B] rounded-md flex items-center justify-center text-white font-bold text-2xl">
                      {supplier.company_name.charAt(0)}
                    </div>
                    {supplier.verified && (
                      <Badge className="bg-[#10B981] hover:bg-[#10B981]">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {t('suppliers.verified')}
                      </Badge>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">
                    {supplier.company_name}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                    <MapPin className="w-4 h-4" />
                    {supplier.location}
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {supplier.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {supplier.categories.slice(0, 3).map((cat) => (
                      <Badge key={cat} variant="outline" className="text-xs">
                        {cat}
                      </Badge>
                    ))}
                    {supplier.categories.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{supplier.categories.length - 3}
                      </Badge>
                    )}
                  </div>
                  
                  <Link to={`/suppliers/${supplier.id}`}>
                    <Button variant="outline" className="w-full">
                      {t('suppliers.viewProfile')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Suppliers;

